import { useState, useRef, useEffect } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap, FeatureGroup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";
import "./DevelopmentMap.css";
import { 
  Search,
  RefreshCw, 
  Filter, 
  Plus,
  Minus,
  MapPin,
  Loader2,
  X,
  ExternalLink,
  Bell,
  BookOpen,
  PlusCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api";

// Fix Leaflet default icon issue
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Hack to fix leaflet marker icons in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Component to update map view only when explicitly needed (not on user drag)
// Based on React Leaflet best practices - only update when props actually change
function MapUpdater({ center, zoom, mapRef, onMapReady }: { center: [number, number]; zoom: number; mapRef?: React.MutableRefObject<L.Map | null>; onMapReady?: () => void }) {
  const map = useMap();
  const prevCenterRef = React.useRef<[number, number] | null>(null);
  const prevZoomRef = React.useRef<number | null>(null);
  const hasInitializedRef = React.useRef(false);

  // Initialize map ref and notify when ready (only once)
  React.useEffect(() => {
    if (mapRef) {
      mapRef.current = map;
    }
    if (onMapReady && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Small delay to ensure map is fully initialized
      setTimeout(() => {
        onMapReady();
      }, 100);
    }
  }, [map, mapRef, onMapReady]);

  // Only update view when center or zoom actually changes (not on user interaction)
  // Use refs to track previous values and only update if significantly different
  React.useEffect(() => {
    const centerChanged = !prevCenterRef.current || 
      Math.abs(prevCenterRef.current[0] - center[0]) > 0.0001 || 
      Math.abs(prevCenterRef.current[1] - center[1]) > 0.0001;
    const zoomChanged = prevZoomRef.current === null || prevZoomRef.current !== zoom;

    // Only setView if center or zoom actually changed from previous values
    // This prevents resetting the map when user drags it
    if (centerChanged || zoomChanged) {
      // Check if the change is significant (not just a tiny drift from user interaction)
      if (prevCenterRef.current) {
        const distance = L.latLng(center[0], center[1])
          .distanceTo(L.latLng(prevCenterRef.current[0], prevCenterRef.current[1]));
        // Only update if it's a significant change (more than 100m) or zoom changed
        // This allows programmatic updates but prevents resetting on user drag
        if (distance > 100 || zoomChanged) {
          map.setView(center, zoom);
        }
      } else {
        // First render - set initial view
        map.setView(center, zoom);
      }
      
      prevCenterRef.current = center;
      prevZoomRef.current = zoom;
    }
  }, [map, center, zoom]);

  return null;
}

// Helper function to create hexagon coordinates
function createHexagon(center: [number, number], radiusKm: number): [number, number][] {
  const [lat, lng] = center;
  const points: [number, number][] = [];
  const radiusDeg = radiusKm / 111; // Rough conversion: 1 degree ≈ 111 km
  
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    const pointLat = lat + radiusDeg * Math.cos(angle);
    const pointLng = lng + radiusDeg * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
    points.push([pointLat, pointLng]);
  }
  points.push(points[0]); // Close the hexagon
  return points;
}

// Helper function to check if a point is inside a polygon
function isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Helper function to categorize status into Ongoing, Approved, or Refused
function categorizeStatus(status: string | null | undefined): 'ongoing' | 'approved' | 'refused' | null {
  if (!status) return null;
  const str = String(status).toUpperCase();
  
  // Approved statuses
  if (str.includes('ADOPTED') || str.includes('AMENDED') || str.includes('APPROVED')) {
    return 'approved';
  }
  
  // Refused statuses
  if (str.includes('NOTADOPT') || str.includes('WITHDRAW') || str.includes('DENIED')) {
    return 'refused';
  }
  
  // Everything else is ongoing
  return 'ongoing';
}

// Helper function to capitalize first letter and lowercase the rest, with special handling for status codes
function capitalizeStatus(status: string | null | undefined): string {
  if (!status) return '';
  const str = String(status);
  if (str.length === 0) return '';
  
  // Map status codes to readable names
  const statusMap: Record<string, string> = {
    'WO_RECS': 'Without Recommendations',
    'NOTADOPT': 'Not Adopted',
    'WITHDRAW': 'Withdrawn',
    'ADOPTED': 'Adopted',
    'AMENDED': 'Amended',
    'APPROVED': 'Approved',
    'POSTPONE': 'Postponed',
    'REFERRED': 'Referred',
  };
  
  const upperStr = str.toUpperCase();
  if (statusMap[upperStr]) {
    return statusMap[upperStr];
  }
  
  // Default: capitalize first letter and lowercase the rest
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Component for leaflet-draw integration with hexagon support
function DrawingControls({ 
  onDrawCreated, 
  featureGroupRef,
  drawControlRef
}: { 
  onDrawCreated: (geoJSON: any, layer?: L.Layer) => void;
  featureGroupRef: React.RefObject<L.FeatureGroup | null>;
  drawControlRef: React.MutableRefObject<L.Control.Draw | null>;
}) {
  const map = useMap();
  const centerMarkerRef = useRef<L.Marker | null>(null);
  const hexagonShapeRef = useRef<L.Polygon | null>(null);
  const isDrawingHexagonRef = useRef(false);

  useEffect(() => {
    if (!featureGroupRef.current) return;

    // Create draw control but DON'T add it to map - we'll use custom buttons instead
    const drawControl = new L.Control.Draw({
      position: 'bottomright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: false, // Disabled - causes "type is not defined" error in leaflet-draw
          shapeOptions: {
            color: '#3b82f6',
            weight: 4,
            opacity: 0.5,
            fill: true,
            fillOpacity: 0.2,
            clickable: true
          }
        },
        rectangle: {
          showArea: false, // Disabled - causes "type is not defined" error in leaflet-draw
          shapeOptions: {
            color: '#3b82f6',
            weight: 4,
            opacity: 0.5,
            fill: true,
            fillOpacity: 0.2,
            clickable: true
          }
        },
        circle: {
          shapeOptions: {
            color: '#3b82f6',
            weight: 4,
            opacity: 0.5,
            fill: true,
            fillOpacity: 0.2,
            clickable: true
          }
        },
        marker: false,
        circlemarker: false,
        polyline: false
      },
      edit: {
        featureGroup: featureGroupRef.current,
        remove: true
      }
    });

    drawControlRef.current = drawControl;
    // Add to map temporarily to initialize handlers, then remove toolbar
    map.addControl(drawControl);
    
    // Store the edit handler reference
    setTimeout(() => {
      const drawControlAny = drawControl as any;
      if (drawControlAny._modes && drawControlAny._modes.edit) {
        (map as any)._editHandler = drawControlAny._modes.edit.handler;
      }
    }, 100);
    
    // Immediately and continuously hide Leaflet's default toolbar containers
    const hideLeafletContainers = () => {
      const leafletTop = document.querySelector('.leaflet-top.leaflet-right');
      const leafletBottom = document.querySelector('.leaflet-bottom.leaflet-right');
      if (leafletTop) {
        (leafletTop as HTMLElement).style.display = 'none';
        (leafletTop as HTMLElement).style.visibility = 'hidden';
        (leafletTop as HTMLElement).style.opacity = '0';
        (leafletTop as HTMLElement).style.pointerEvents = 'none';
      }
      if (leafletBottom) {
        (leafletBottom as HTMLElement).style.display = 'none';
        (leafletBottom as HTMLElement).style.visibility = 'hidden';
        (leafletBottom as HTMLElement).style.opacity = '0';
        (leafletBottom as HTMLElement).style.pointerEvents = 'none';
      }
    };
    
    // Hide immediately and keep hiding
    hideLeafletContainers();
    setTimeout(hideLeafletContainers, 50);
    setTimeout(hideLeafletContainers, 200);
    setInterval(hideLeafletContainers, 500);

    // Handle standard draw events
    map.on(L.Draw.Event.CREATED, (e: any) => {
      const layer = e.layer;
      featureGroupRef.current?.addLayer(layer);
      const geoJSON = layer.toGeoJSON();
      onDrawCreated(geoJSON, layer);
    });


    // Show instructions when polygon drawing starts
    map.on(L.Draw.Event.DRAWSTART, (e: any) => {
      if (e.layerType === 'polygon') {
        // Show instruction tooltip
        const instructionDiv = L.DomUtil.create('div', 'draw-instruction');
        instructionDiv.innerHTML = 'Click points on the map. Double-click to finish and close the polygon.';
        instructionDiv.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); color: white; padding: 12px 20px; border-radius: 8px; z-index: 2000; pointer-events: none; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';
        map.getContainer().appendChild(instructionDiv);
        
        // Remove instruction after 3 seconds
        setTimeout(() => {
          if (instructionDiv.parentNode) {
            instructionDiv.parentNode.removeChild(instructionDiv);
          }
        }, 3000);
      }
    });

    // Remove instruction when drawing stops
    map.on(L.Draw.Event.DRAWSTOP, () => {
      const instruction = document.querySelector('.draw-instruction');
      if (instruction) {
        instruction.remove();
      }
    });

    // Hexagon drawing logic
    const startHexagonDrawing = () => {
      isDrawingHexagonRef.current = true;
      map.on('click', handleHexagonClick);
      map.on('mousemove', handleHexagonMouseMove);
    };

    const handleHexagonClick = (e: L.LeafletMouseEvent) => {
      if (!isDrawingHexagonRef.current) return;

      if (!centerMarkerRef.current) {
        // First click - set center
        centerMarkerRef.current = L.marker(e.latlng, {
          icon: L.divIcon({
            className: 'hexagon-center-marker',
            html: '<div style="width: 12px; height: 12px; background: #3b82f6; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
            iconSize: [12, 12],
            iconAnchor: [6, 6]
          })
        }).addTo(map);
      } else {
        // Second click - complete hexagon
        const center = centerMarkerRef.current.getLatLng();
        const radius = map.distance(center, e.latlng) / 1000; // km
        const hexagonCoords = createHexagon([center.lat, center.lng], radius);
        
        const layer = L.polygon(hexagonCoords, {
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          weight: 2
        });
        
        featureGroupRef.current?.addLayer(layer);
        const geoJSON = layer.toGeoJSON();
        onDrawCreated(geoJSON);
        
        // Cleanup
        if (hexagonShapeRef.current) map.removeLayer(hexagonShapeRef.current);
        if (centerMarkerRef.current) map.removeLayer(centerMarkerRef.current);
        centerMarkerRef.current = null;
        hexagonShapeRef.current = null;
        isDrawingHexagonRef.current = false;
        map.off('click', handleHexagonClick);
        map.off('mousemove', handleHexagonMouseMove);
      }
    };

    const handleHexagonMouseMove = (e: L.LeafletMouseEvent) => {
      if (!isDrawingHexagonRef.current || !centerMarkerRef.current) return;
      
      const center = centerMarkerRef.current.getLatLng();
      const radius = map.distance(center, e.latlng) / 1000; // km
      const hexagonCoords = createHexagon([center.lat, center.lng], radius);
      
      if (hexagonShapeRef.current) {
        map.removeLayer(hexagonShapeRef.current);
      }
      
      hexagonShapeRef.current = L.polygon(hexagonCoords, {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.2,
        weight: 2,
        dashArray: '5, 5'
      }).addTo(map);
    };

    // Add custom buttons (hexagon and pen/polygon) to toolbar
    const addCustomButtons = () => {
      const toolbar = document.querySelector('.leaflet-draw-toolbar');
      if (toolbar) {
        // Add Pen/Polygon button (makes polygon tool more obvious)
        const existingPenButton = toolbar.querySelector('.leaflet-draw-draw-pen');
        if (!existingPenButton) {
          const penButton = L.DomUtil.create('a', 'leaflet-draw-draw-pen');
          penButton.href = '#';
          penButton.title = 'Pen Tool - Click points to draw a polygon';
          penButton.innerHTML = '✏️';
          // Styling is handled by CSS
          
          L.DomEvent.on(penButton, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            // Trigger polygon drawing
            const polygonButton = toolbar.querySelector('.leaflet-draw-draw-polygon') as HTMLElement;
            if (polygonButton) {
              polygonButton.click();
            }
          });
          
          toolbar.appendChild(penButton);
        }

        // Add hexagon button
        const existingHexagonButton = toolbar.querySelector('.leaflet-draw-draw-hexagon');
        if (!existingHexagonButton) {
          const hexagonButton = L.DomUtil.create('a', 'leaflet-draw-draw-hexagon');
          hexagonButton.href = '#';
          hexagonButton.title = 'Draw hexagon - Click center, then click to set radius';
          hexagonButton.innerHTML = '⬡';
          // Styling is handled by CSS
          
          L.DomEvent.on(hexagonButton, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            startHexagonDrawing();
          });
          
          toolbar.appendChild(hexagonButton);
        }
      }
    };

    // Toolbar draggable functionality removed - using custom buttons instead

    // Hide any Leaflet toolbars that might appear
    const hideLeafletToolbars = () => {
      const allToolbars = document.querySelectorAll('.leaflet-draw-toolbar-top, .leaflet-draw-toolbar-bottom');
      allToolbars.forEach((toolbar) => {
        (toolbar as HTMLElement).style.display = 'none';
        (toolbar as HTMLElement).style.visibility = 'hidden';
      });
      
      const leafletTopRight = document.querySelector('.leaflet-top.leaflet-right');
      const leafletBottomRight = document.querySelector('.leaflet-bottom.leaflet-right');
      if (leafletTopRight) {
        (leafletTopRight as HTMLElement).style.display = 'none';
        (leafletTopRight as HTMLElement).style.visibility = 'hidden';
      }
      if (leafletBottomRight) {
        (leafletBottomRight as HTMLElement).style.display = 'none';
        (leafletBottomRight as HTMLElement).style.visibility = 'hidden';
      }
    };
    
    // Continuously hide Leaflet toolbars
    const hideInterval = setInterval(hideLeafletToolbars, 100);
    (window as any)._hideToolbarInterval = hideInterval;

    // Hide toolbars immediately and continuously
    hideLeafletToolbars();
    setTimeout(hideLeafletToolbars, 50);
    setTimeout(hideLeafletToolbars, 200);

    return () => {
      // Cancel any active drawing modes before cleanup
      try {
        if (drawControlRef.current) {
          // Cancel any active drawing mode
          const drawControl = drawControlRef.current as any;
          if (drawControl._activeMode && drawControl._activeMode.handler) {
            drawControl._activeMode.handler.disable();
          }
          // Remove the control
          map.removeControl(drawControlRef.current);
        }
      } catch (error) {
        // Ignore errors during cleanup
        console.warn('Error cleaning up draw control:', error);
      }
      
      // Clean up hexagon drawing state
      if (isDrawingHexagonRef.current) {
        map.off('click', handleHexagonClick);
        map.off('mousemove', handleHexagonMouseMove);
        isDrawingHexagonRef.current = false;
      }
      
      // Clean up draggable toolbar
      const toolbarContainer = document.querySelector('.leaflet-draw-toolbar-top') as any;
      if (toolbarContainer && toolbarContainer._dragCleanup) {
        toolbarContainer._dragCleanup();
      }

      // Clear hide toolbar interval
      if ((window as any)._hideToolbarInterval) {
        clearInterval((window as any)._hideToolbarInterval);
      }
      
      // Hide all Leaflet toolbars
      hideLeafletToolbars();
      
      // Remove event listeners
      map.off(L.Draw.Event.CREATED);
      map.off(L.Draw.Event.DRAWSTART);
      map.off(L.Draw.Event.DRAWSTOP);
      map.off('draw:drawstart', addCustomButtons);
      map.off('draw:editstart');
      map.off('draw:drawstop');
      map.off('draw:editstop');
      map.off('click', handleHexagonClick);
      map.off('mousemove', handleHexagonMouseMove);
      
      // Remove hexagon markers and shapes
      if (hexagonShapeRef.current) {
        try {
          map.removeLayer(hexagonShapeRef.current);
        } catch (e) {
          // Ignore if already removed
        }
      }
      if (centerMarkerRef.current) {
        try {
          map.removeLayer(centerMarkerRef.current);
        } catch (e) {
          // Ignore if already removed
        }
      }
    };
  }, [map, onDrawCreated, featureGroupRef, drawControlRef]);

  return null;
}

// Component to render markers with clustering
function MarkersGroup({ applications, onViewDetails, applicationsInHexagons, searchResultIds }: { applications: any[]; onViewDetails?: (app: any) => void; applicationsInHexagons?: Set<string>; searchResultIds?: Set<string> }) {
  // Custom cluster icon function to ensure no numbers on individual markers
  const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();
    let size = 'small';
    if (count > 100) size = 'large';
    else if (count > 20) size = 'medium';

    return L.divIcon({
      html: `<div style="background-color: rgba(59, 130, 246, 0.6); color: white; border-radius: 50%; border: 3px solid white; width: ${size === 'large' ? '50px' : size === 'medium' ? '40px' : '30px'}; height: ${size === 'large' ? '50px' : size === 'medium' ? '40px' : '30px'}; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: ${size === 'large' ? '16px' : size === 'medium' ? '14px' : '12px'}; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${count}</div>`,
      className: 'marker-cluster-custom',
      iconSize: L.point(size === 'large' ? 50 : size === 'medium' ? 40 : 30, size === 'large' ? 50 : size === 'medium' ? 40 : 30)
    });
  };

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={60}
      spiderfyOnMaxZoom={true}
      showCoverageOnHover={false}
      iconCreateFunction={createClusterCustomIcon}
    >
      {applications.map((app: any, index: number) => (
        <CustomMarker 
          key={`marker-${app.id}-${index}`} 
          position={app.position} 
          status={app.status}
          property={app}
          onViewDetails={() => onViewDetails?.(app)}
          isInHexagon={applicationsInHexagons?.has(app.id)}
          isSearchResult={searchResultIds?.has(app.id)}
        />
      ))}
    </MarkerClusterGroup>
  );
}

// Popup content component - ensures content always renders
const PopupContent = ({ property, status, onViewDetails }: { property: any; status: string; onViewDetails?: () => void }) => {
  // Ensure we always have valid data
  if (!property) {
    return (
      <div className="p-3 min-w-[200px]">
        <h3 className="font-bold text-sm mb-2">Development Application</h3>
        <p className="text-xs text-neutral-400">No details available</p>
      </div>
    );
  }

  const title = property?.name || property?.title || property?.reference || 'Development Application';
  const reference = property?.reference;
  const address = property?.address;
  const allAddresses = property?.allAddresses || [];
  const hasMultipleAddresses = property?.hasMultipleAddresses || (allAddresses.length > 1);
  const municipality = property?.municipality_name || property?.municipality;
  const buildingType = property?.type || property?.building_type;
  const storeys = property?.storeys;
  const units = property?.units;
  const appItemStatus = status || '';
  const displayStatus = capitalizeStatus(appItemStatus);
  const statusUpper = appItemStatus ? String(appItemStatus).toUpperCase() : '';
  const badgeVariant = statusUpper.includes('ADOPTED') || statusUpper.includes('AMENDED') || statusUpper.includes('APPROVED') 
    ? "default" 
    : statusUpper.includes('NOTADOPT') || statusUpper.includes('WITHDRAW') || statusUpper.includes('DENIED')
    ? "destructive"
    : "secondary";

  return (
    <div className="p-3 min-w-[200px] max-w-[300px]">
      <h3 className="font-bold text-sm mb-2">{title}</h3>
      
      {reference && (
        <p className="text-xs text-neutral-400 mb-2 font-mono">Ref: {reference}</p>
      )}
      
      
      {/* Show all addresses if multiple */}
      {hasMultipleAddresses && allAddresses.length > 0 ? (
        <div className="mb-2">
          <p className="text-xs font-semibold text-neutral-600 mb-1">
            📍 Addresses ({allAddresses.length}):
          </p>
          <div className="max-h-24 overflow-y-auto space-y-1">
            {allAddresses.map((addr: any, idx: number) => {
              const addrText = addr?.address || addr?.street || addr?.full_address || 'Unknown Address';
              return (
                <p key={idx} className="text-xs text-neutral-600 bg-neutral-50 p-1 rounded">
                  {addrText}
                </p>
              );
            })}
          </div>
        </div>
      ) : address ? (
        <p className="text-xs text-neutral-600 mb-2">{address}</p>
      ) : (
        <p className="text-xs text-neutral-400 mb-2 italic">No address available</p>
      )}
      
      {municipality && (
        <p className="text-xs text-neutral-500 mb-2">{municipality}</p>
      )}
      
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {displayStatus && (
          <Badge variant={badgeVariant} className="text-[10px]">
            {displayStatus}
          </Badge>
        )}
        {buildingType && (
          <span className="text-xs text-neutral-500">{buildingType}</span>
        )}
        {storeys && (
          <span className="text-xs text-neutral-500">{storeys} storeys</span>
        )}
        {units && (
          <span className="text-xs text-neutral-500">{units} units</span>
        )}
      </div>
      <Button 
        size="sm" 
        className="w-full h-7 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          if (onViewDetails) {
            onViewDetails();
          }
        }}
      >
        View Details
      </Button>
    </div>
  );
};

// Custom marker component - ensures no numbers on markers
const CustomMarker = ({ position, status, property, onViewDetails, isInHexagon, isSearchResult }: any) => {
  // Ensure we have valid status and property
  const markerStatus = status || '';
  const markerProperty = property || {};
  
  // Determine color based on item_status (use neutral colors for unknown statuses)
  let colorClass = "bg-blue-500"; // Default neutral color
  if (markerStatus) {
    const statusUpper = String(markerStatus).toUpperCase();
    if (statusUpper.includes('ADOPTED') || statusUpper.includes('AMENDED') || statusUpper.includes('APPROVED')) {
      colorClass = "bg-green-500";
    } else if (statusUpper.includes('NOTADOPT') || statusUpper.includes('WITHDRAW') || statusUpper.includes('DENIED')) {
      colorClass = "bg-red-500";
    } else if (statusUpper.includes('POSTPONE') || statusUpper.includes('REFERRED') || statusUpper.includes('WO_RECS')) {
      colorClass = "bg-orange-400";
    } else {
      colorClass = "bg-blue-500"; // Default for other statuses
    }
  }

  // Add pulsing effect if in hexagon or search result
  const pulseClass = (isInHexagon || isSearchResult) ? "animate-pulse" : "";

  // Create icon with NO numbers - just a solid colored circle
  // Use React.useMemo to ensure icon is created fresh and doesn't cache old versions
  const icon = React.useMemo(() => {
    let borderColor = "white";
    let borderWidth = 2;
    if (isInHexagon) {
      borderColor = "#3b82f6";
      borderWidth = 3;
    } else if (isSearchResult) {
      borderColor = "#fbbf24"; // Yellow/gold for search results
      borderWidth = 3;
    }
    return L.divIcon({
      className: "custom-marker",
      html: `<div class="${colorClass} ${pulseClass} w-8 h-8 rounded-full border-${borderWidth} border-[${borderColor}] shadow-lg" style="display: flex; align-items: center; justify-content: center; font-size: 0; line-height: 0; border-color: ${borderColor};"></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  }, [colorClass, isInHexagon, isSearchResult, pulseClass]);

  return (
    <Marker 
      position={position}
      icon={icon}
    >
      <Popup 
        maxWidth={400} 
        className="custom-popup" 
        autoPan={true}
        eventHandlers={{
          add: (e) => {
            // Ensure popup content is visible when added
            const popup = e.target;
            if (popup && popup._contentNode) {
              popup._contentNode.style.minHeight = '50px';
            }
          }
        }}
      >
        <PopupContent property={markerProperty} status={markerStatus} onViewDetails={onViewDetails} />
      </Popup>
    </Marker>
  );
};

export default function MapPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [zoom, setZoom] = useState(11);
  const [selectedApplication, setSelectedApplication] = useState<any | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Watch areas state
  const [watchAreas, setWatchAreas] = useState<any[]>([]);
  const [applicationsInWatchAreas, setApplicationsInWatchAreas] = useState<Set<string>>(new Set());
  const [showWatchAreaNotification, setShowWatchAreaNotification] = useState(false);
  const [watchAreaNameDialog, setWatchAreaNameDialog] = useState<{ open: boolean; geoJSON: any | null }>({ open: false, geoJSON: null });
  const [watchAreaName, setWatchAreaName] = useState("");
  const [selectedWatchAreaModal, setSelectedWatchAreaModal] = useState<{ open: boolean; watchArea: any | null; applications: any[] }>({ open: false, watchArea: null, applications: [] });
  const watchAreaLayersRef = useRef<Map<number, L.Layer>>(new Map()); // Track loaded watch area layers by ID
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCenter, setSearchCenter] = useState<[number, number] | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  
  // Status filters: Ongoing, Approved, Refused
  const [statusFilters, setStatusFilters] = useState({
    ongoing: true,
    approved: true,
    refused: true,
  });
  const [buildingTypeFilters, setBuildingTypeFilters] = useState({
    residential: true,
    mixed: true,
    commercial: true,
    townhouse: true,
  });
  const [storeysRange, setStoreysRange] = useState<[number, number]>([0, 100]);

  // Check if company has any municipalities monitoring
  const { data: monitoredMunicipalities } = useQuery({
    queryKey: ['company-municipalities-all'],
    queryFn: async () => {
      try {
        const data = await apiClient.companyMunicipalities.listAll();
        return data;
      } catch (error) {
        console.error('[DevelopmentMap] Error fetching municipalities:', error);
        return [];
      }
    },
  });

  // Fetch development applications data from the view (each row is one point on the map)
  const { data: mapData, isLoading, error, refetch: refetchMapData } = useQuery({
    queryKey: ['development-applications-map'],
    queryFn: () => apiClient.developmentApplications.map(undefined, undefined, undefined, 1000),
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    // Use default refetchOnMount: false (from queryClient defaults)
    // Use default refetchOnWindowFocus: false (from queryClient defaults)
  });

  // Fetch watch areas
  const { data: watchAreasData, refetch: refetchWatchAreas } = useQuery({
    queryKey: ['watch-areas'],
    queryFn: () => apiClient.watchAreas.list(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    // Use default refetchOnMount: false (from queryClient defaults)
    // Use default refetchOnWindowFocus: false (from queryClient defaults)
  });

  // Track recently deleted IDs to prevent them from being re-added by refetch
  const recentlyDeletedRef = useRef<Set<number>>(new Set());
  const deletionTimestampsRef = useRef<Map<number, number>>(new Map()); // Track when each ID was deleted
  
  // Load watch areas when data is fetched
  useEffect(() => {
    console.log('🟡 watchAreasData changed:', watchAreasData);
    if (watchAreasData && Array.isArray(watchAreasData)) {
      // Filter out any recently deleted boundaries that might still be in the refetch
      // Keep them filtered for at least 5 seconds to ensure database deletion completes
      const now = Date.now();
      const filtered = watchAreasData.filter(wa => {
        const deletedAt = deletionTimestampsRef.current.get(wa.id);
        if (deletedAt && (now - deletedAt) < 5000) {
          console.log(`🚫 Filtering out recently deleted watch area ${wa.id} (deleted ${now - deletedAt}ms ago)`);
          return false;
        }
        // Clean up old deletion timestamps
        if (deletedAt && (now - deletedAt) >= 5000) {
          deletionTimestampsRef.current.delete(wa.id);
          recentlyDeletedRef.current.delete(wa.id);
        }
        return true;
      });
      console.log(`🟢 Setting ${filtered.length} watch areas to state (filtered from ${watchAreasData.length})`);
      setWatchAreas(filtered);
    } else if (watchAreasData === undefined || watchAreasData === null) {
      console.log('⚠️ watchAreasData is null/undefined, clearing watch areas');
      setWatchAreas([]);
    }
  }, [watchAreasData]);

  // Clean up delete markers when watch areas are loaded/re-rendered
  // This ensures delete markers don't persist after refresh
  useEffect(() => {
    if (mapRef.current && watchAreas.length >= 0) {
      const map = mapRef.current;
      // Clean up any existing delete markers from previous sessions
      const existingDeleteMarkers = (map as any)._customDeleteMarkers || [];
      if (existingDeleteMarkers.length > 0) {
        existingDeleteMarkers.forEach((marker: L.Marker) => {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        });
        (map as any)._customDeleteMarkers = [];
      }
    }
  }, [watchAreas]);

  // Track previous watch areas to detect changes
  const prevWatchAreasRef = useRef<Map<number, any>>(new Map());
  
  // Render watch areas on the map when they're loaded from the database
  // Use incremental updates to avoid flashing - based on React Leaflet best practices
  useEffect(() => {
    const featureGroup = featureGroupRef.current;
    const map = mapRef.current;
    const watchAreaLayers = watchAreaLayersRef.current;
    
    if (!map || !featureGroup) {
      // Map not ready yet, will retry when map becomes available
      return;
    }

    // Clean up any existing delete markers
    const existingDeleteMarkers = (map as any)._customDeleteMarkers || [];
    if (existingDeleteMarkers.length > 0) {
      existingDeleteMarkers.forEach((marker: L.Marker) => {
        if (map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      });
      (map as any)._customDeleteMarkers = [];
    }
    
    // Get current and previous watch area IDs
    const currentWatchAreaIds = new Set(watchAreas.map(wa => wa.id));
    const prevWatchAreaIds = new Set(prevWatchAreasRef.current.keys());
    
    // Remove layers for watch areas that no longer exist (incremental removal)
    const idsToRemove = Array.from(prevWatchAreaIds).filter(id => !currentWatchAreaIds.has(id));
    idsToRemove.forEach(id => {
      const layer = watchAreaLayers.get(id);
      if (layer) {
        // Remove from feature group and map
        if (featureGroup.hasLayer(layer)) {
          featureGroup.removeLayer(layer);
        }
        if (map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
        // Remove from tracking maps
        watchAreaLayers.delete(id);
        prevWatchAreasRef.current.delete(id);
        console.log(`🗑️ Removed watch area ${id} from map and refs`);
      }
    });
    
      // Add or update watch areas (incremental addition/update)
      watchAreas.forEach((watchArea) => {
        const watchAreaGeoJSON = watchArea.geojson || watchArea.geometry;
        
        if (!watchAreaGeoJSON || !watchArea.id) {
          return;
        }

        // Check if this watch area already exists and hasn't changed
        const prevWatchArea = prevWatchAreasRef.current.get(watchArea.id);
        if (prevWatchArea && JSON.stringify(prevWatchArea.geojson) === JSON.stringify(watchAreaGeoJSON)) {
          // Watch area unchanged, skip re-rendering
          return;
        }

        // Remove existing layer if it exists (for update case)
        const existingLayer = watchAreaLayers.get(watchArea.id);
        if (existingLayer) {
          if (featureGroup.hasLayer(existingLayer)) {
            featureGroup.removeLayer(existingLayer);
          }
          if (map.hasLayer(existingLayer)) {
            map.removeLayer(existingLayer);
          }
          watchAreaLayers.delete(watchArea.id);
        }
      
      console.log('🔄 Processing watch area:', watchArea.id, watchArea.name);

      try {
        // Handle both full GeoJSON objects and geometry-only objects
        let geometryToRender;
        if (watchAreaGeoJSON.type === 'Feature') {
          // Full GeoJSON Feature - extract geometry
          geometryToRender = watchAreaGeoJSON.geometry;
        } else if (watchAreaGeoJSON.type === 'FeatureCollection') {
          // GeoJSON FeatureCollection - use first feature's geometry
          geometryToRender = watchAreaGeoJSON.features?.[0]?.geometry;
        } else if (watchAreaGeoJSON.type && ['Point', 'Polygon', 'LineString', 'MultiPolygon', 'MultiPoint', 'MultiLineString'].includes(watchAreaGeoJSON.type)) {
          // Direct geometry object
          geometryToRender = watchAreaGeoJSON;
        } else {
          console.warn('⚠️ Unknown GeoJSON structure:', watchAreaGeoJSON);
          return;
        }

        if (!geometryToRender) {
          console.warn('⚠️ Could not extract geometry from watch area:', watchArea.id);
          return;
        }

        console.log('🎨 Rendering geometry:', geometryToRender);

        // Create a GeoJSON layer from the watch area geometry
        const geoJSONLayer = L.geoJSON(geometryToRender as any, {
          style: {
            color: '#3388ff',
            weight: 3,
            opacity: 0.8,
            fillColor: '#3388ff',
            fillOpacity: 0.2
          }
        });

        // Store the watch area ID on the layer itself
        (geoJSONLayer as any)._watchAreaId = watchArea.id;
        (geoJSONLayer as any)._watchAreaName = watchArea.name;
        (geoJSONLayer as any)._watchAreaData = watchArea;
        
        // Track if we've added at least one layer for this watch area
        let layerAdded = false;
        
        // Add the layer to both the map and the feature group
        // This allows them to be edited and deleted
        geoJSONLayer.eachLayer((layer: any) => {
          // Store the watch area ID and name in the layer for reference
          (layer as any)._watchAreaId = watchArea.id;
          (layer as any)._watchAreaName = watchArea.name;
          (layer as any)._watchAreaData = watchArea;
          
          console.log('✅ Adding layer to map and feature group:', watchArea.id, watchArea.name);
          
          // Add click handler to open modal with applications in this watch area
          layer.on('click', () => {
            // Find applications within this watch area
            const appsInArea: any[] = [];
            if (filteredApplications && mapRef.current) {
              filteredApplications.forEach((app: any) => {
                const point: [number, number] = app.position;
                
                // Extract geometry from watch area
                const clickGeoJSON = watchArea.geojson || watchArea.geometry;
                if (!clickGeoJSON) return;
                
                let geometry = clickGeoJSON;
                if (clickGeoJSON.type === 'Feature') {
                  geometry = clickGeoJSON.geometry;
                } else if (clickGeoJSON.type === 'FeatureCollection' && clickGeoJSON.features?.[0]) {
                  geometry = clickGeoJSON.features[0].geometry;
                }
                
                if (geometry && geometry.coordinates) {
                  let coords: [number, number][];
                  
                  if (geometry.type === 'Polygon') {
                    coords = geometry.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]);
                    if (isPointInPolygon(point, coords)) {
                      appsInArea.push(app);
                    }
                  } else if (geometry.type === 'Point') {
                    const [lng, lat] = geometry.coordinates;
                    const distance = L.latLng(lat, lng).distanceTo(L.latLng(point[0], point[1])) / 1000; // km
                    if (distance <= (watchArea.radius || 1)) {
                      appsInArea.push(app);
                    }
                  }
                }
              });
            }
            
            // Open modal with applications
            setSelectedWatchAreaModal({
              open: true,
              watchArea: watchArea,
              applications: appsInArea
            });
          });
          
          // Add to feature group (for edit/delete functionality)
          featureGroup.addLayer(layer);
          
          // Add to map
          map.addLayer(layer);
          
          // Store the layer reference - use the first layer as the representative
          if (!layerAdded) {
            watchAreaLayers.set(watchArea.id, layer);
            layerAdded = true;
          }
        });
        
        // Store in previous ref for change detection
        prevWatchAreasRef.current.set(watchArea.id, { ...watchArea });
      } catch (error) {
        console.error('❌ Error rendering watch area:', watchArea.id, error);
      }
    });
    
    // No cleanup needed - we handle removal incrementally above
    // filteredApplications is used in click handler but doesn't need to be in deps
    // since the handler will use the latest value from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchAreas]);
  
  // Force refetch watch areas when component mounts to ensure fresh data
  useEffect(() => {
    console.log('🟡 Component mounted, forcing watch areas refetch...');
    refetchWatchAreas();
  }, [refetchWatchAreas]); // Only run on mount

  // Handle deletion of watch areas when they're removed from the map (Leaflet Draw delete)
  useEffect(() => {
    if (!mapRef.current) return;

    const handleDelete = async (e: any) => {
      const layers = e.layers;
      layers.eachLayer(async (layer: any) => {
        // Check if this layer has a watch area ID (loaded from database)
        const watchAreaId = (layer as any)._watchAreaId;
        if (watchAreaId) {
          try {
            const numericId = typeof watchAreaId === 'string' ? parseInt(watchAreaId, 10) : watchAreaId;
            
                      // Mark as recently deleted to prevent refetch from re-adding it
                      const deletedAt = Date.now();
                      recentlyDeletedRef.current.add(numericId);
                      deletionTimestampsRef.current.set(numericId, deletedAt);
                      
                      // Optimistically update state
                      setWatchAreas((prev) => {
                        const filtered = prev.filter((wa) => wa.id !== numericId);
                        // Also update prevWatchAreasRef immediately
                        prevWatchAreasRef.current.delete(numericId);
                        return filtered;
                      });
                      
                      // Remove from tracking map
                      watchAreaLayersRef.current.delete(numericId);
                      
                      // Delete from database
                      await apiClient.watchAreas.delete(numericId);
                      
                      // Invalidate and refetch to sync
                      queryClient.invalidateQueries({ queryKey: ['watch-areas'] });
                      setTimeout(async () => {
                        await refetchWatchAreas();
                        // Keep in recently deleted for at least 5 seconds total
                        const timeSinceDeletion = Date.now() - deletedAt;
                        setTimeout(() => {
                          recentlyDeletedRef.current.delete(numericId);
                          deletionTimestampsRef.current.delete(numericId);
                        }, Math.max(0, 5000 - timeSinceDeletion));
                      }, 1000);
          } catch (error) {
            console.error('Failed to delete watch area from database:', error);
            // Revert optimistic update on error
            await refetchWatchAreas();
          }
        }
      });
    };

    const map = mapRef.current;
    map.on(L.Draw.Event.DELETED, handleDelete);

    return () => {
      if (map) {
        map.off(L.Draw.Event.DELETED, handleDelete);
      }
    };
  }, [refetchWatchAreas, queryClient]);

  // Transform development applications data to map format - each row from the view is one point
  const applications = React.useMemo(() => {
    if (!mapData || !Array.isArray(mapData)) {
      return [];
    }
    
    const mappedApps: any[] = [];
    
    // Process each row from the view - each row has latitude and longitude directly
    for (const app of mapData) {
      const lat = app.latitude;
      const lng = app.longitude;
      
      // Check if coordinates are valid
      if (
        lat != null && 
        lng != null && 
        typeof lat === 'number' && 
        typeof lng === 'number' &&
        !isNaN(lat) && 
        !isNaN(lng) &&
        lat >= -90 && lat <= 90 &&
        lng >= -180 && lng <= 180
      ) {
        // Get item_status - use directly, don't map to simplified status
        let itemStatus = '';
        if (app.item_status) {
          // Handle both string and array cases
          if (Array.isArray(app.item_status)) {
            // Use the first status if it's an array
            itemStatus = app.item_status[0] || '';
          } else if (typeof app.item_status === 'string') {
            itemStatus = app.item_status;
          } else {
            itemStatus = String(app.item_status);
          }
        }
        
        mappedApps.push({
          id: app.id || `${app.reference || 'unknown'}-${Date.now()}`,
          position: [lat, lng] as [number, number],
          status: itemStatus, // Use item_status directly
          type: app.building_type || 'Mixed-Use',
          building_type: app.building_type, // Keep original for filtering
          count: app.units || app.storeys || 1,
          name: app.title || app.reference || 'Development Application',
          address: app.address || 'Unknown Address',
          allAddresses: [{ address: app.address }], // Single address per row now
          hasMultipleAddresses: false, // Each row is one address
          municipality_name: app.municipality,
          reference: app.reference,
          storeys: app.storeys || 0,
          units: app.units,
          item_status: app.item_status,
          meeting_date: app.meeting_date,
          ...app
        });
      }
    }
    
    return mappedApps;
  }, [mapData]);


  // Filter applications based on filter state and search
  const filteredApplications = React.useMemo(() => {
    // If searching, prioritize search results
    let appsToFilter = applications;
    if (searchQuery.trim() && searchResults.length > 0) {
      // When searching, show search results that also match filters
      appsToFilter = searchResults;
    }
    
    // If no status filters are selected, show nothing
    const hasAnyStatusFilter = Object.values(statusFilters).some(v => v);
    if (!hasAnyStatusFilter) {
      return [];
    }
    
    // If no building type filters are selected, show nothing
    const hasAnyTypeFilter = Object.values(buildingTypeFilters).some(v => v);
    if (!hasAnyTypeFilter) {
      return [];
    }
    
    return appsToFilter.filter((app: any) => {
      // Filter by status category (Ongoing, Approved, Refused)
      const appItemStatus = app.item_status || app.status || '';
      const statusStr = Array.isArray(appItemStatus) ? appItemStatus[0] : String(appItemStatus);
      const statusCategory = categorizeStatus(statusStr);
      
      // Check if this status category is enabled in filters
      if (!statusCategory || !statusFilters[statusCategory]) {
        return false;
      }
      
      // Filter by building type
      const appType = (app.type || app.building_type || '').toLowerCase().trim();
      let matchesType = false;
      
      // Check each building type filter
      if (buildingTypeFilters.residential) {
        if (appType.includes('residential') || appType.includes('res') || appType === 'residential') {
          matchesType = true;
        }
      }
      if (buildingTypeFilters.mixed) {
        if (appType.includes('mixed') || appType.includes('mix') || appType === 'mixed-use' || appType === 'mixed use') {
          matchesType = true;
        }
      }
      if (buildingTypeFilters.commercial) {
        if (appType.includes('commercial') || appType.includes('comm') || appType === 'commercial') {
          matchesType = true;
        }
      }
      if (buildingTypeFilters.townhouse) {
        if (appType.includes('townhouse') || appType.includes('town') || appType === 'townhouse') {
          matchesType = true;
        }
      }
      
      // If app has no type or unknown type, only show if all building type filters are checked
      if (!appType || appType === '' || appType === 'unknown') {
        const allTypeFiltersChecked = Object.values(buildingTypeFilters).every(v => v);
        if (allTypeFiltersChecked) {
          matchesType = true;
        }
      }
      
      // If no type matches, exclude this app
      if (!matchesType) {
        return false;
      }
      
      // Filter by storeys - must be within range
      const storeys = app.storeys || 0;
      if (storeys < storeysRange[0] || storeys > storeysRange[1]) {
        return false;
      }
      
      return true;
    });
  }, [applications, statusFilters, buildingTypeFilters, storeysRange, searchQuery, searchResults]);

  // Geocode address using Nominatim (OpenStreetMap)
  const geocodeAddress = async (address: string): Promise<[number, number] | null> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'CityScrape Development Map'
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        return [lat, lng];
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Handle address search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchCenter(null);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.trim().toLowerCase();

    // Filter applications by address - check multiple address fields
    const matchingApps = applications.filter((app: any) => {
      // Check main address field
      const mainAddress = (app.address || '').toLowerCase();
      if (mainAddress.includes(query)) return true;
      
      // Check all addresses array if it exists
      if (app.allAddresses && Array.isArray(app.allAddresses)) {
        for (const addr of app.allAddresses) {
          const addrText = (addr?.address || addr?.street || addr?.full_address || '').toLowerCase();
          if (addrText.includes(query)) return true;
        }
      }
      
      // Check if addresses field exists (from API)
      if (app.addresses && Array.isArray(app.addresses)) {
        for (const addr of app.addresses) {
          const addrText = (typeof addr === 'string' ? addr : addr?.address || '').toLowerCase();
          if (addrText.includes(query)) return true;
        }
      }
      
      return false;
    });

    setSearchResults(matchingApps);

    // If we found applications, zoom to the first one
    if (matchingApps.length > 0) {
      const firstApp = matchingApps[0];
      setSearchCenter(firstApp.position);
      setZoom(16); // Zoom in closer for search results
    } else {
      // Try to geocode the address to see if it exists
      const coords = await geocodeAddress(searchQuery);
      if (coords) {
        setSearchCenter(coords);
        setZoom(16);
        // Show a message that no applications were found but we geocoded the address
        console.log(`Geocoded "${searchQuery}" but no applications found at that location`);
      } else {
        // No results found
        alert(`No applications found for "${searchQuery}"`);
      }
    }

    setIsSearching(false);
  };

  // Handle Enter key in search
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Track initial center - only update on explicit search, not on filteredApplications changes
  const [mapCenter, setMapCenter] = React.useState<[number, number]>([43.70, -79.38]); // Default Toronto center
  
  // Update center only when search is performed (explicit user action)
  React.useEffect(() => {
    if (searchCenter) {
      setMapCenter(searchCenter);
    }
  }, [searchCenter]);
  
  // Don't auto-update center when filteredApplications changes - let user control the map

  // Track previous notification count to only show when it increases
  const prevNotificationCountRef = useRef<number>(0);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check which applications are within watch areas
  React.useEffect(() => {
    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    if (watchAreas.length === 0) {
      setApplicationsInWatchAreas(new Set());
      prevNotificationCountRef.current = 0;
      return;
    }

    const appsInWatchAreas = new Set<string>();

    filteredApplications.forEach((app: any) => {
      const point: [number, number] = app.position;
      
      for (const watchArea of watchAreas) {
        // API returns 'geojson' field, not 'geometry'
        const geoJSONData = watchArea.geojson || watchArea.geometry;
        if (!geoJSONData) continue;
        
        // Extract geometry from GeoJSON structure
        let geometry = geoJSONData;
        if (geoJSONData.type === 'Feature') {
          geometry = geoJSONData.geometry;
        } else if (geoJSONData.type === 'FeatureCollection' && geoJSONData.features?.[0]) {
          geometry = geoJSONData.features[0].geometry;
        }
        
        if (geometry && geometry.coordinates) {
          let coords: [number, number][];
          
          // Handle different geometry types
          if (geometry.type === 'Polygon') {
            coords = geometry.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]);
          } else if (geometry.type === 'Point') {
            // For point, check distance (could be a circle)
            const [lng, lat] = geometry.coordinates;
            const distance = L.latLng(lat, lng).distanceTo(L.latLng(point[0], point[1])) / 1000; // km
            if (distance <= (watchArea.radius || 1)) {
              appsInWatchAreas.add(app.id);
              break;
            }
            continue;
          } else {
            continue;
          }
          
          if (isPointInPolygon(point, coords)) {
            appsInWatchAreas.add(app.id);
            break;
          }
        }
      }
    });

    const currentCount = appsInWatchAreas.size;
    const prevCount = prevNotificationCountRef.current;
    
    setApplicationsInWatchAreas(appsInWatchAreas);
    
    // Only show notification if count increased (new applications found)
    // Don't show if count decreased or stayed the same
    if (currentCount > 0 && currentCount > prevCount) {
      setShowWatchAreaNotification(true);
      // Auto-hide after 5 seconds
      notificationTimeoutRef.current = setTimeout(() => {
        setShowWatchAreaNotification(false);
        notificationTimeoutRef.current = null;
      }, 5000);
    }
    
    // Update previous count
    prevNotificationCountRef.current = currentCount;

    // Cleanup timeout on unmount or when effect re-runs
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [watchAreas, filteredApplications]);

  // Store the last created layer so we can remove it if cancelled
  const lastCreatedLayerRef = useRef<L.Layer | null>(null);

  // Handle draw created
  const handleDrawCreated = (geoJSON: any, layer?: L.Layer) => {
    // Store the layer so we can remove it if user cancels
    if (layer) {
      lastCreatedLayerRef.current = layer;
    }
    setWatchAreaNameDialog({ open: true, geoJSON });
  };

  // Save watch area
  const saveWatchArea = async () => {
    if (!watchAreaName.trim() || !watchAreaNameDialog.geoJSON) return;
    
    try {
      await apiClient.watchAreas.create({
        name: watchAreaName.trim(),
        geometry: watchAreaNameDialog.geoJSON.geometry,
        municipality: 'Toronto' // Could be determined from map center
      });
      // Clear the last created layer ref since we're saving it
      lastCreatedLayerRef.current = null;
      setWatchAreaNameDialog({ open: false, geoJSON: null });
      setWatchAreaName("");
      // Force refetch watch areas query to ensure data is fresh
      await refetchWatchAreas();
    } catch (error) {
      console.error('Failed to save watch area:', error);
    }
  };

  // Check if user has any municipalities monitoring
  const hasMunicipalities = monitoredMunicipalities && monitoredMunicipalities.length > 0;

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F4F5F7] dark:bg-neutral-950">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-neutral-500">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F4F5F7] dark:bg-neutral-950">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-red-500">Error loading properties</p>
          <p className="text-xs text-neutral-500">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-hidden">
      <main className="flex-1 relative h-full w-full">
        {!hasMunicipalities && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#F4F5F7] dark:bg-neutral-950 p-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl bg-neutral-50/50 dark:bg-neutral-900/50"
            >
              <div className="h-16 w-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                No Development Applications Found
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400 max-w-md mb-8 leading-relaxed">
                You aren't monitoring any municipalities yet. Configure your monitoring criteria to start receiving updates on regulations and bills.
              </p>
              <div className="flex items-center gap-4">
                <Button variant="outline" className="gap-2 bg-white dark:bg-neutral-900">
                  Learn More
                </Button>
            <Button className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white" onClick={() => navigate('/sources')}>
              <PlusCircle className="h-4 w-4" />
              Configure Monitoring
            </Button>
              </div>
            </motion.div>
          </div>
        )}
        {/* Map Container */}
        <div className="absolute inset-0 z-0">
           <MapContainer 
             center={mapCenter} 
             zoom={zoom} 
             style={{ height: "100%", width: "100%" }}
             zoomControl={false}
           >
             <MapUpdater 
               center={mapCenter} 
               zoom={zoom} 
               mapRef={mapRef}
               onMapReady={() => {
                 // Force watch areas to re-render when map becomes ready
                 if (watchAreas.length > 0) {
                   console.log('🗺️ Map is ready, triggering watch areas render...');
                   setWatchAreas([...watchAreas]);
                 }
               }}
             />
             <TileLayer
               attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
               url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
             />
             <FeatureGroup ref={featureGroupRef}>
               <DrawingControls 
                 onDrawCreated={handleDrawCreated}
                 featureGroupRef={featureGroupRef}
                 drawControlRef={drawControlRef}
               />
               {/* Watch areas are rendered via useEffect using L.geoJSON for better control */}
             </FeatureGroup>
             <MarkersGroup 
               applications={filteredApplications} 
               onViewDetails={(app) => {
                 setSelectedApplication(app);
                 setDetailModalOpen(true);
               }}
               applicationsInHexagons={applicationsInWatchAreas}
               searchResultIds={searchQuery.trim() ? new Set(searchResults.map((app: any) => app.id)) : undefined}
             />
           </MapContainer>
        </div>

        {/* Top Left: Search and Refresh */}
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <div className="w-80 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-hidden flex items-center">
            <Input 
              placeholder="Search address..." 
              className="border-none shadow-none focus-visible:ring-0 h-10 rounded-none"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Clear search results when input changes
                if (!e.target.value.trim()) {
                  setSearchResults([]);
                  setSearchCenter(null);
                }
              }}
              onKeyDown={handleSearchKeyDown}
              disabled={isSearching}
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                  setSearchCenter(null);
                }}
                className="h-10 w-8 flex items-center justify-center text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div 
              className="h-10 w-12 bg-blue-500 flex items-center justify-center text-white hover:bg-blue-600 cursor-pointer"
              onClick={handleSearch}
            >
               {isSearching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            </div>
          </div>
          
          {/* Refresh Button */}
          <Button
            onClick={() => {
              refetchMapData();
              queryClient.invalidateQueries({ queryKey: ['development-applications-map'] });
            }}
            variant="outline"
            size="icon"
            className="h-10 w-10 bg-white shadow-lg border-neutral-200"
            title="Refresh applications data"
          >
            <RefreshCw className="h-5 w-5" />
          </Button>
        </div>
        {/* Search Results Indicator */}
        {searchQuery && searchResults.length > 0 && (
          <div className="absolute top-20 left-4 z-10 bg-white rounded-lg shadow-lg border border-neutral-200 p-3">
            <p className="text-sm text-neutral-600">
              Found <span className="font-semibold">{searchResults.length}</span> application{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          </div>
        )}

        {/* Left Panel: Filter */}
        <div className="absolute top-20 left-4 z-10 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-hidden flex flex-col">
           <div className="p-3 border-b border-neutral-100 flex items-center gap-2">
             <Filter className="h-4 w-4 text-neutral-500" />
             <span className="font-semibold text-sm">Filter Applications</span>
           </div>
           
           <div className="p-4 space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto">
              {/* Status Section */}
              <div className="space-y-3">
                 <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</h4>
                 <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="status-ongoing"
                        checked={statusFilters.ongoing}
                        onCheckedChange={(checked) => setStatusFilters(prev => ({ ...prev, ongoing: checked as boolean }))}
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                      />
                      <Label htmlFor="status-ongoing" className="text-sm font-normal cursor-pointer">Ongoing</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="status-approved"
                        checked={statusFilters.approved}
                        onCheckedChange={(checked) => setStatusFilters(prev => ({ ...prev, approved: checked as boolean }))}
                        className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <Label htmlFor="status-approved" className="text-sm font-normal cursor-pointer">Approved</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="status-refused"
                        checked={statusFilters.refused}
                        onCheckedChange={(checked) => setStatusFilters(prev => ({ ...prev, refused: checked as boolean }))}
                        className="data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                      />
                      <Label htmlFor="status-refused" className="text-sm font-normal cursor-pointer">Refused</Label>
                    </div>
                 </div>
              </div>

              {/* Type Section */}
              <div className="space-y-3">
                 <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Building Type</h4>
                 <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="residential" 
                        checked={buildingTypeFilters.residential}
                        onCheckedChange={(checked) => setBuildingTypeFilters(prev => ({ ...prev, residential: checked as boolean }))}
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                      />
                      <Label htmlFor="residential" className="text-sm font-normal cursor-pointer">Residential</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="mixed" 
                        checked={buildingTypeFilters.mixed}
                        onCheckedChange={(checked) => setBuildingTypeFilters(prev => ({ ...prev, mixed: checked as boolean }))}
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                      />
                      <Label htmlFor="mixed" className="text-sm font-normal cursor-pointer">Mixed-Use</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="commercial" 
                        checked={buildingTypeFilters.commercial}
                        onCheckedChange={(checked) => setBuildingTypeFilters(prev => ({ ...prev, commercial: checked as boolean }))}
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                      />
                      <Label htmlFor="commercial" className="text-sm font-normal cursor-pointer">Commercial</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="townhouse" 
                        checked={buildingTypeFilters.townhouse}
                        onCheckedChange={(checked) => setBuildingTypeFilters(prev => ({ ...prev, townhouse: checked as boolean }))}
                        className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                      />
                      <Label htmlFor="townhouse" className="text-sm font-normal cursor-pointer">Townhouse</Label>
                    </div>
                 </div>
              </div>

              {/* Height Section */}
              <div className="space-y-4">
                 <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Height (Storeys)</h4>
                 <div className="px-2">
                    <Slider 
                      value={storeysRange} 
                      onValueChange={(value) => setStoreysRange(value as [number, number])}
                      max={100} 
                      step={1} 
                      className="my-4" 
                    />
                 </div>
                 <div className="flex items-center justify-between text-xs text-neutral-500">
                    <div className="bg-neutral-100 px-2 py-1 rounded border border-neutral-200">{storeysRange[0]}</div>
                    <span>to</span>
                    <div className="bg-neutral-100 px-2 py-1 rounded border border-neutral-200">{storeysRange[1]}</div>
                 </div>
              </div>

              <div className="pt-2 space-y-1">
                 <div className="text-xs text-center text-neutral-400">
                   Showing {filteredApplications.length} of {applications.length} {applications.length === 1 ? 'property' : 'properties'}
                 </div>
                 {watchAreas.length > 0 && applicationsInWatchAreas.size > 0 && (
                   <div className="text-xs text-center text-blue-600 font-medium">
                     <Bell className="h-3 w-3 inline mr-1" />
                     {applicationsInWatchAreas.size} in watch area{watchAreas.length > 1 ? 's' : ''}
                   </div>
                 )}
              </div>
           </div>
        </div>

        {/* Top Right: Summary Card */}
        <div className="absolute top-4 right-4 z-10 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 p-4">
           <div className="flex items-center gap-2 mb-2">
             <span className="text-orange-500 text-lg">🏗️</span>
             <h3 className="font-bold text-sm">Development Applications</h3>
           </div>
           <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-neutral-500">Total Applications:</span>
                <span className="font-medium">{mapData?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">With Coordinates:</span>
                <span className="font-medium">{applications.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Filtered:</span>
                <span className="font-medium">{filteredApplications.length}</span>
              </div>
              {mapData && mapData.length > 0 && (
                <>
                  {statusFilters.ongoing && (() => {
                    const count = filteredApplications.filter((a: any) => {
                      const appStatus = a.item_status || a.status || '';
                      const statusStr = Array.isArray(appStatus) ? appStatus[0] : String(appStatus);
                      return categorizeStatus(statusStr) === 'ongoing';
                    }).length;
                    if (count === 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Ongoing:</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })()}
                  {statusFilters.approved && (() => {
                    const count = filteredApplications.filter((a: any) => {
                      const appStatus = a.item_status || a.status || '';
                      const statusStr = Array.isArray(appStatus) ? appStatus[0] : String(appStatus);
                      return categorizeStatus(statusStr) === 'approved';
                    }).length;
                    if (count === 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Approved:</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })()}
                  {statusFilters.refused && (() => {
                    const count = filteredApplications.filter((a: any) => {
                      const appStatus = a.item_status || a.status || '';
                      const statusStr = Array.isArray(appStatus) ? appStatus[0] : String(appStatus);
                      return categorizeStatus(statusStr) === 'refused';
                    }).length;
                    if (count === 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Refused:</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })()}
                </>
              )}
           </div>
        </div>

        {/* Bottom Status Bar with Controls */}
        <div className="bottom-status-bar-container absolute bottom-4 left-4 right-4 z-10 bg-white rounded-lg shadow-lg border border-neutral-200 p-3 flex items-center gap-4 overflow-x-auto">
          {/* Application Status Section */}
          <>
            <span className="font-semibold text-sm whitespace-nowrap">Application Status</span>
            
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-xs text-neutral-600 whitespace-nowrap">Ongoing</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-xs text-neutral-600 whitespace-nowrap">Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-neutral-600 whitespace-nowrap">Refused</span>
            </div>
            
            {/* Divider */}
            <div className="h-6 w-px bg-neutral-300 mx-2" />
          </>
          
          {/* Custom Drawing and Edit Toolbars */}
          <div id="draw-edit-toolbars-container" className="flex items-center gap-2">
            {/* Draw Tools */}
            <div className="flex items-center gap-1">
              <button
                className="h-7 w-7 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 flex items-center justify-center"
                title="Draw Polygon"
                onClick={() => {
                  if (mapRef.current && featureGroupRef.current && drawControlRef.current) {
                    console.log('🔷 Polygon button clicked');
                    
                    // Disable any existing handlers first
                    if ((mapRef.current as any)._drawHandlers) {
                      Object.values((mapRef.current as any)._drawHandlers).forEach((h: any) => {
                        if (h && h.disable) {
                          console.log('Disabling existing handler');
                          h.disable();
                        }
                      });
                    }
                    
                    // Try to use the drawControl's polygon handler first
                    const drawControl = drawControlRef.current as any;
                    console.log('DrawControl:', drawControl);
                    console.log('DrawControl._toolbars:', drawControl._toolbars);
                    console.log('DrawControl._modes:', drawControl._modes);
                    
                    // Attempt 1: Try using the draw control's toolbar
                    if (drawControl._toolbars && drawControl._toolbars.draw) {
                      const drawToolbar = drawControl._toolbars.draw;
                      console.log('Draw toolbar:', drawToolbar);
                      
                      if (drawToolbar._modes && drawToolbar._modes.polygon) {
                        console.log('Found polygon mode in toolbar, activating...');
                        drawToolbar._modes.polygon.handler.enable();
                        return;
                      }
                    }
                    
                    // Attempt 2: Create new handler with minimal config (matching DrawControl)
                    console.log('Creating new polygon handler...');
                    const handler = new (L as any).Draw.Polygon(mapRef.current, {
                      allowIntersection: false,
                      showArea: true
                    });
                    
                    console.log('Handler created:', handler);
                    console.log('Handler._markers:', handler._markers);
                    console.log('Handler._finishShape:', handler._finishShape);
                    
                    // CRITICAL FIX: Prevent auto-completion by intercepting the completeShape method
                    const originalCompleteShape = handler.completeShape;
                    const originalFinishShape = handler._finishShape;
                    
                    if (originalCompleteShape) {
                      handler.completeShape = function(...args: any[]) {
                        console.log('⚠️ completeShape called, marker count:', handler._markers?.length);
                        if (handler._markers && handler._markers.length < 3) {
                          console.log('Blocking completion - need at least 3 points');
                          return;
                        }
                        return originalCompleteShape.apply(this, args);
                      };
                    }
                    
                    if (originalFinishShape) {
                      handler._finishShape = function(...args: any[]) {
                        console.log('⚠️ _finishShape called, marker count:', handler._markers?.length);
                        // Only allow finishing if explicitly called (not auto-triggered)
                        return originalFinishShape.apply(this, args);
                      };
                    }
                    
                    // Monitor vertex additions
                    const originalAddVertex = handler.addVertex || handler._onClick;
                    if (originalAddVertex) {
                      const wrappedAddVertex = function(...args: any[]) {
                        console.log('➕ Adding vertex, current count:', handler._markers?.length);
                        const result = originalAddVertex.apply(this, args);
                        console.log('   After adding, count:', handler._markers?.length);
                        return result;
                      };
                      if (handler.addVertex) {
                        handler.addVertex = wrappedAddVertex;
                      } else {
                        handler._onClick = wrappedAddVertex;
                      }
                    }
                    
                    // Store handler
                    if (!(mapRef.current as any)._drawHandlers) {
                      (mapRef.current as any)._drawHandlers = {};
                    }
                    (mapRef.current as any)._drawHandlers.polygon = handler;
                    
                    // Enable the handler
                    handler.enable();
                    console.log('Handler enabled');
                  }
                }}
              >
                <span className="text-sm">⬟</span>
              </button>
              <button
                className="h-7 w-7 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 flex items-center justify-center"
                title="Draw Rectangle"
                onClick={() => {
                  if (mapRef.current && featureGroupRef.current) {
                    // Disable any existing handlers first
                    if ((mapRef.current as any)._drawHandlers) {
                      Object.values((mapRef.current as any)._drawHandlers).forEach((h: any) => {
                        if (h && h.disable) h.disable();
                      });
                    }
                    
                    // Create rectangle handler
                    const handler = new (L as any).Draw.Rectangle(mapRef.current, {
                      showArea: true,
                      repeatMode: false,
                      shapeOptions: {
                        stroke: true,
                        color: '#3b82f6',
                        weight: 4,
                        opacity: 0.5,
                        fill: true,
                        fillColor: null,
                        fillOpacity: 0.2,
                        clickable: true
                      },
                      metric: true
                    });
                    
                    // Store handler
                    if (!(mapRef.current as any)._drawHandlers) {
                      (mapRef.current as any)._drawHandlers = {};
                    }
                    (mapRef.current as any)._drawHandlers.rectangle = handler;
                    
                    // Enable the handler
                    handler.enable();
                  }
                }}
              >
                <span className="text-sm">▭</span>
              </button>
              <button
                className="h-7 w-7 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 flex items-center justify-center"
                title="Draw Circle"
                onClick={() => {
                  if (mapRef.current && featureGroupRef.current) {
                    // Disable any existing handlers first
                    if ((mapRef.current as any)._drawHandlers) {
                      Object.values((mapRef.current as any)._drawHandlers).forEach((h: any) => {
                        if (h && h.disable) h.disable();
                      });
                    }
                    
                    // Create circle handler
                    const handler = new (L as any).Draw.Circle(mapRef.current, {
                      repeatMode: false,
                      shapeOptions: {
                        stroke: true,
                        color: '#3b82f6',
                        weight: 4,
                        opacity: 0.5,
                        fill: true,
                        fillColor: null,
                        fillOpacity: 0.2,
                        clickable: true
                      },
                      showRadius: true,
                      metric: true,
                      feet: true,
                      nautic: false
                    });
                    
                    // Store handler
                    if (!(mapRef.current as any)._drawHandlers) {
                      (mapRef.current as any)._drawHandlers = {};
                    }
                    (mapRef.current as any)._drawHandlers.circle = handler;
                    
                    // Enable the handler
                    handler.enable();
                  }
                }}
              >
                <span className="text-sm">○</span>
              </button>
            </div>
            
            {/* Edit Tools */}
            {watchAreas.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                <button
                  className="h-7 w-7 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 flex items-center justify-center"
                  title="Edit Boundaries"
                  onClick={() => {
                    if (mapRef.current && featureGroupRef.current && drawControlRef.current) {
                      // Disable any draw handlers
                      if ((mapRef.current as any)._drawHandlers) {
                        Object.values((mapRef.current as any)._drawHandlers).forEach((h: any) => {
                          if (h && h.disable) h.disable();
                        });
                      }
                      // Use the edit handler from draw control
                      const drawControl = drawControlRef.current as any;
                      // Wait a bit for draw control to initialize if needed
                      const enableEdit = () => {
                        if (drawControl._modes && drawControl._modes.edit && drawControl._modes.edit.handler) {
                          drawControl._modes.edit.handler.enable();
                          return true;
                        }
                        return false;
                      };
                      
                      if (!enableEdit()) {
                        // Try again after a short delay
                        setTimeout(() => {
                          if (!enableEdit()) {
                            console.warn('Edit handler not available yet');
                          }
                        }, 200);
                      }
                    }
                  }}
                >
                  <span className="text-sm">✏️</span>
                </button>
                <button
                  className="h-7 w-7 bg-white border border-red-200 rounded-md hover:bg-red-50 flex items-center justify-center text-red-600"
                  title="Delete Boundaries - Click to show delete markers on each boundary"
                  onClick={() => {
                    if (!mapRef.current || !featureGroupRef.current) {
                      return;
                    }
                    
                    // Disable any draw handlers
                    if ((mapRef.current as any)._drawHandlers) {
                      Object.values((mapRef.current as any)._drawHandlers).forEach((h: any) => {
                        if (h && h.disable) h.disable();
                      });
                    }
                    
                    // Check if delete markers are already showing
                    const existingMarkers = (mapRef.current as any)._customDeleteMarkers || [];
                    if (existingMarkers.length > 0) {
                      // Delete mode is active - remove all delete markers
                      existingMarkers.forEach((marker: L.Marker) => {
                        mapRef.current?.removeLayer(marker);
                      });
                      (mapRef.current as any)._customDeleteMarkers = [];
                      return;
                    }
                    
                    // Check if there are any boundaries
                    let hasLayers = false;
                    featureGroupRef.current.eachLayer(() => {
                      hasLayers = true;
                    });
                    
                    if (!hasLayers) {
                      alert('No boundaries to delete.');
                      return;
                    }
                    
                    // Initialize array to store custom delete markers
                    if (!(mapRef.current as any)._customDeleteMarkers) {
                      (mapRef.current as any)._customDeleteMarkers = [];
                    }
                    
                    // Add a single delete marker (red circle with X) for each boundary
                    featureGroupRef.current.eachLayer((layer: any) => {
                      // Capture the layer reference and watch area ID in the closure
                      const layerRef = layer;
                      const watchAreaId = (layerRef as any)._watchAreaId;
                      
                      // Skip if no watch area ID
                      if (!watchAreaId) {
                        console.warn('Layer has no watchAreaId, skipping delete marker');
                        return;
                      }
                      
                      // Get the center of the layer
                      let center: L.LatLng;
                      if (layerRef.getBounds && layerRef.getBounds().isValid()) {
                        center = layerRef.getBounds().getCenter();
                      } else if (layerRef.getLatLng) {
                        center = layerRef.getLatLng();
                      } else {
                        console.warn('Cannot get center for layer, skipping delete marker');
                        return; // Skip if we can't get a position
                      }
                      
                      // Create a custom delete marker (red circle with X)
                      const deleteIcon = L.divIcon({
                        className: 'custom-delete-marker',
                        html: '<div style="width: 30px; height: 30px; background-color: #ef4444; border: 3px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); cursor: pointer;"><span style="color: white; font-size: 18px; font-weight: bold; line-height: 1;">×</span></div>',
                        iconSize: [30, 30],
                        iconAnchor: [15, 15]
                      });
                      
                      const deleteMarker = L.marker(center, { icon: deleteIcon, zIndexOffset: 1000 });
                      
                      // Store the watch area ID on the marker for reference
                      (deleteMarker as any)._watchAreaId = watchAreaId;
                      (deleteMarker as any)._layerRef = layerRef;
                      
                      // Add click handler to delete the entire boundary
                      deleteMarker.on('click', async () => {
                        const idToDelete = (deleteMarker as any)._watchAreaId;
                        
                        console.log('Delete marker clicked, watchAreaId:', idToDelete);
                        
                        if (!idToDelete) {
                          console.error('No watch area ID found on delete marker');
                          alert('Error: Could not identify watch area to delete.');
                          return;
                        }
                        
                        try {
                          console.log('Attempting to delete watch area:', idToDelete);
                          // Delete from database - ensure it's a number
                          const numericId = typeof idToDelete === 'string' ? parseInt(idToDelete, 10) : idToDelete;
                          if (isNaN(numericId)) {
                            throw new Error(`Invalid watch area ID: ${idToDelete}`);
                          }
                          
                          // First, remove ALL layers associated with this watch area from the map
                          const layersToRemove: any[] = [];
                          if (featureGroupRef.current) {
                            featureGroupRef.current.eachLayer((layer: any) => {
                              if ((layer as any)._watchAreaId === numericId) {
                                layersToRemove.push(layer);
                              }
                            });
                          }
                          
                          // Also check the stored layer reference
                          const storedLayer = watchAreaLayersRef.current.get(numericId);
                          if (storedLayer && !layersToRemove.includes(storedLayer)) {
                            layersToRemove.push(storedLayer);
                          }
                          
                          console.log(`Found ${layersToRemove.length} layers to remove for watch area ${numericId}`);
                          
                          // Remove all layers from feature group and map
                          layersToRemove.forEach((layer) => {
                            if (featureGroupRef.current) {
                              featureGroupRef.current.removeLayer(layer);
                            }
                            if (mapRef.current) {
                              mapRef.current.removeLayer(layer);
                            }
                          });
                          
                          // Remove from our tracking map
                          watchAreaLayersRef.current.delete(numericId);
                          
                          // Mark as recently deleted to prevent refetch from re-adding it
                          const deletedAt = Date.now();
                          recentlyDeletedRef.current.add(numericId);
                          deletionTimestampsRef.current.set(numericId, deletedAt);
                          
                          // Update state immediately to prevent re-rendering
                          setWatchAreas((prev) => {
                            const filtered = prev.filter((wa) => wa.id !== numericId);
                            // Also update prevWatchAreasRef immediately to prevent re-rendering
                            prevWatchAreasRef.current.delete(numericId);
                            return filtered;
                          });
                          
                          // Delete from database
                          await apiClient.watchAreas.delete(numericId);
                          console.log('Successfully deleted watch area from database');
                          
                          // Remove the delete marker from the map
                          if (mapRef.current) {
                            mapRef.current.removeLayer(deleteMarker);
                          }
                          
                          // Remove the delete marker from our array
                          const markers = (mapRef.current as any)._customDeleteMarkers || [];
                          const index = markers.indexOf(deleteMarker);
                          if (index > -1) {
                            markers.splice(index, 1);
                          }
                          
                          // Invalidate and refetch watch areas query to ensure data is fresh
                          // Use invalidateQueries instead of refetch to avoid race conditions
                          queryClient.invalidateQueries({ queryKey: ['watch-areas'] });
                          // Longer delay before refetch to ensure database deletion is complete
                          setTimeout(async () => {
                            await refetchWatchAreas();
                            // Keep in recently deleted for at least 5 seconds total
                            const timeSinceDeletion = Date.now() - deletedAt;
                            setTimeout(() => {
                              recentlyDeletedRef.current.delete(numericId);
                              deletionTimestampsRef.current.delete(numericId);
                            }, Math.max(0, 5000 - timeSinceDeletion));
                          }, 1000);
                          
                          // If no more boundaries, remove all remaining delete markers
                          let hasRemainingLayers = false;
                          featureGroupRef.current?.eachLayer(() => {
                            hasRemainingLayers = true;
                          });
                          if (!hasRemainingLayers) {
                            const remainingMarkers = (mapRef.current as any)._customDeleteMarkers || [];
                            remainingMarkers.forEach((m: L.Marker) => {
                              mapRef.current?.removeLayer(m);
                            });
                            (mapRef.current as any)._customDeleteMarkers = [];
                          }
                        } catch (error: any) {
                          console.error('Failed to delete watch area from database:', error);
                          console.error('Error details:', {
                            message: error?.message,
                            response: error?.response?.data,
                            status: error?.response?.status,
                            watchAreaId: idToDelete
                          });
                          alert(`Failed to delete watch area: ${error?.response?.data?.detail || error?.message || 'Unknown error'}. Please try again.`);
                        }
                      });
                      
                      if (mapRef.current) {
                        deleteMarker.addTo(mapRef.current);
                        (mapRef.current as any)._customDeleteMarkers.push(deleteMarker);
                      }
                    });
                  }}
                >
                  <span className="text-sm">🗑️</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Divider before zoom */}
          <div className="h-6 w-px bg-neutral-300 mx-2" />
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-neutral-500 whitespace-nowrap mr-2">Zoom:</span>
            <div className="flex flex-row gap-1">
              <Button variant="secondary" size="icon" className="bg-white shadow-md border border-neutral-200 rounded-md h-7 w-7 hover:bg-neutral-50" onClick={() => setZoom(z => z + 1)}>
                <Plus className="h-3 w-3" />
              </Button>
              <Button variant="secondary" size="icon" className="bg-white shadow-md border border-neutral-200 rounded-md h-7 w-7 hover:bg-neutral-50" onClick={() => setZoom(z => z - 1)}>
                <Minus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Watch Area Notification */}
        {showWatchAreaNotification && applicationsInWatchAreas.size > 0 && (
          <div className="absolute top-20 right-4 z-20 bg-blue-500 text-white rounded-lg shadow-lg border border-blue-600 p-4 max-w-sm animate-in slide-in-from-top-2">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-1">Applications in Watch Area</h4>
                <p className="text-xs text-blue-50">
                  {applicationsInWatchAreas.size} {applicationsInWatchAreas.size === 1 ? 'application' : 'applications'} 
                  {applicationsInWatchAreas.size === 1 ? ' is' : ' are'} within your watch area{watchAreas.length > 1 ? 's' : ''}.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-white hover:bg-blue-600 flex-shrink-0"
                onClick={() => setShowWatchAreaNotification(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* No data message */}
        {!isLoading && !error && mapData && mapData.length > 0 && applications.length === 0 && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-white rounded-lg shadow-lg border border-neutral-200 p-6 max-w-md">
            <div className="flex flex-col items-center gap-3 text-center">
              <MapPin className="h-12 w-12 text-neutral-400" />
              <h3 className="font-semibold text-sm text-neutral-900">No Applications with Coordinates</h3>
              <p className="text-xs text-neutral-500">
                You have {mapData.length} {mapData.length === 1 ? 'development application' : 'development applications'}, but none have valid coordinates in their addresses. 
                Please ensure addresses have latitude and longitude to view them on the map.
              </p>
            </div>
          </div>
        )}

        {!isLoading && !error && (!mapData || mapData.length === 0) && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-white rounded-lg shadow-lg border border-neutral-200 p-6 max-w-md">
            <div className="flex flex-col items-center gap-3 text-center">
              <MapPin className="h-12 w-12 text-neutral-400" />
              <h3 className="font-semibold text-sm text-neutral-900">No Development Applications Found</h3>
              <p className="text-xs text-neutral-500">
                No development applications found in the database.
              </p>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Development Application Details</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setDetailModalOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    {selectedApplication.title || selectedApplication.name || 'Development Application'}
                  </h3>
                  {selectedApplication.reference && (
                    <p className="text-sm text-neutral-500 font-mono mb-2">
                      Reference: {selectedApplication.reference}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Address</p>
                    <p className="text-sm">{selectedApplication.address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Municipality</p>
                    <p className="text-sm">{selectedApplication.municipality_name || selectedApplication.municipality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Status</p>
                    <Badge 
                      variant={(() => {
                        const status = selectedApplication.item_status || selectedApplication.status || '';
                        const statusUpper = String(status).toUpperCase();
                        if (statusUpper.includes('ADOPTED') || statusUpper.includes('AMENDED') || statusUpper.includes('APPROVED')) {
                          return "default";
                        } else if (statusUpper.includes('NOTADOPT') || statusUpper.includes('WITHDRAW') || statusUpper.includes('DENIED')) {
                          return "destructive";
                        }
                        return "secondary";
                      })()}
                      className="text-xs"
                    >
                      {(() => {
                        const status = selectedApplication.item_status || selectedApplication.status || '';
                        if (!status) return '';
                        const str = String(status);
                        if (str.length === 0) return '';
                        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
                      })()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Building Type</p>
                    <p className="text-sm">{selectedApplication.building_type || selectedApplication.type || 'N/A'}</p>
                  </div>
                  {selectedApplication.storeys && (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Storeys</p>
                      <p className="text-sm">{selectedApplication.storeys}</p>
                    </div>
                  )}
                  {selectedApplication.units && (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Units</p>
                      <p className="text-sm">{selectedApplication.units}</p>
                    </div>
                  )}
                  {selectedApplication.meeting_date && (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Meeting Date</p>
                      <p className="text-sm">{new Date(selectedApplication.meeting_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedApplication.decision_body && (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Decision Body</p>
                      <p className="text-sm">{selectedApplication.decision_body}</p>
                    </div>
                  )}
                  {(selectedApplication.planning_application_number || 
                    selectedApplication.planning_number || 
                    selectedApplication.application_number ||
                    selectedApplication.planning_app_number) && (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-1">Planning Application Number</p>
                      <p className="text-sm">
                        {selectedApplication.planning_application_number || 
                         selectedApplication.planning_number || 
                         selectedApplication.application_number ||
                         selectedApplication.planning_app_number}
                      </p>
                    </div>
                  )}
                </div>

                {/* Timeline Section */}
                {(() => {
                  // Handle timeline_items - could be JSONB string or already parsed
                  let timelineItems = selectedApplication.timeline_items;
                  
                  // Debug logging
                  console.log('Timeline items:', timelineItems, 'Type:', typeof timelineItems);
                  
                  if (typeof timelineItems === 'string') {
                    try {
                      timelineItems = JSON.parse(timelineItems);
                    } catch (e) {
                      console.error('Failed to parse timeline_items:', e);
                      timelineItems = null;
                    }
                  }
                  
                  if (!timelineItems || !Array.isArray(timelineItems) || timelineItems.length === 0) {
                    console.log('No timeline items to display');
                    return null;
                  }
                  
                  console.log('Displaying timeline with', timelineItems.length, 'items');
                  
                  // Sort items by date (oldest to latest)
                  const sortedItems = timelineItems.sort((a: any, b: any) => {
                    const dateA = a.date ? new Date(a.date).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
                    const dateB = b.date ? new Date(b.date).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
                    return dateA - dateB; // Oldest to latest
                  });
                  
                  return (
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Timeline</p>
                      <div className="space-y-4 relative pl-4 border-l-2 border-neutral-200">
                        {sortedItems.map((item: any, index: number) => {
                          const itemDate = item.date ? new Date(item.date) : null;
                          const statusColor = 
                            item.status?.toUpperCase().includes('APPROVED') || item.status?.toUpperCase().includes('ADOPTED') ? 'bg-green-100 text-green-800' :
                            item.status?.toUpperCase().includes('DENIED') || item.status?.toUpperCase().includes('WITHDRAW') || item.status?.toUpperCase().includes('NOTADOPT') ? 'bg-red-100 text-red-800' :
                            'bg-orange-100 text-orange-800';
                          
                          return (
                            <div key={item.id || index} className="relative pb-4">
                              {/* Timeline dot */}
                              <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-500 border-2 border-white"></div>
                              
                              <div className="ml-6">
                                <div className="flex items-center gap-2 mb-1">
                                  {itemDate && (
                                    <p className="text-sm font-semibold text-neutral-900">
                                      {itemDate.toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })}
                                    </p>
                                  )}
                                  {item.status && (
                                    <Badge className={`text-xs ${statusColor}`}>
                                      {item.status}
                                    </Badge>
                                  )}
                                </div>
                                
                                {item.reference && (
                                  <p className="text-xs text-neutral-500 font-mono mb-1">
                                    {item.reference}
                                  </p>
                                )}
                                
                                {item.title && (
                                  <p className="text-sm font-medium text-neutral-800 mb-1">
                                    {item.title}
                                  </p>
                                )}
                                
                                {item.summary && (
                                  <div 
                                    className="text-sm text-neutral-600 prose prose-sm max-w-none mb-2"
                                    dangerouslySetInnerHTML={{ __html: item.summary }}
                                  />
                                )}
                                
                                {item.decision_body && (
                                  <p className="text-xs text-neutral-500 mb-2">
                                    Decision Body: {item.decision_body}
                                  </p>
                                )}
                                
                                {item.reference && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => {
                                      const url = `https://secure.toronto.ca/council/agenda-item.do?item=${item.reference}`;
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    View Item
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {selectedApplication.reference && (
                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const url = `https://secure.toronto.ca/council/agenda-item.do?item=${selectedApplication.reference}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View on Toronto City Council Website
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Watch Area Applications Modal */}
        <Dialog open={selectedWatchAreaModal.open} onOpenChange={(open) => !open && setSelectedWatchAreaModal({ open: false, watchArea: null, applications: [] })}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>
                  Applications in "{selectedWatchAreaModal.watchArea?.name || 'Watch Area'}"
                </DialogTitle>
                <Button 
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    const watchArea = selectedWatchAreaModal.watchArea;
                    if (!watchArea || !watchArea.id) return;
                    
                    const confirmed = window.confirm(`Are you sure you want to delete "${watchArea.name}"?`);
                    if (!confirmed) return;
                    
                    try {
                      const numericId = typeof watchArea.id === 'string' ? parseInt(watchArea.id, 10) : watchArea.id;
                      
                      // Mark as recently deleted to prevent refetch from re-adding it
                      const deletedAt = Date.now();
                      recentlyDeletedRef.current.add(numericId);
                      deletionTimestampsRef.current.set(numericId, deletedAt);
                      
                      // Optimistically update state
                      setWatchAreas((prev) => {
                        const filtered = prev.filter((wa) => wa.id !== numericId);
                        // Also update prevWatchAreasRef immediately
                        prevWatchAreasRef.current.delete(numericId);
                        return filtered;
                      });
                      
                      // Find and remove the layer from the map immediately
                      if (featureGroupRef.current && mapRef.current) {
                        featureGroupRef.current.eachLayer((layer: any) => {
                          if ((layer as any)._watchAreaId === numericId) {
                            featureGroupRef.current?.removeLayer(layer);
                            mapRef.current?.removeLayer(layer);
                          }
                        });
                      }
                      
                      // Remove from tracking map
                      watchAreaLayersRef.current.delete(numericId);
                      
                      // Delete from database
                      await apiClient.watchAreas.delete(numericId);
                      
                      // Invalidate and refetch to sync
                      queryClient.invalidateQueries({ queryKey: ['watch-areas'] });
                      setTimeout(async () => {
                        await refetchWatchAreas();
                        // Keep in recently deleted for at least 5 seconds total
                        const timeSinceDeletion = Date.now() - deletedAt;
                        setTimeout(() => {
                          recentlyDeletedRef.current.delete(numericId);
                          deletionTimestampsRef.current.delete(numericId);
                        }, Math.max(0, 5000 - timeSinceDeletion));
                      }, 1000);
                      
                      // Close the modal
                      setSelectedWatchAreaModal({ open: false, watchArea: null, applications: [] });
                    } catch (error) {
                      console.error('Failed to delete watch area:', error);
                      alert('Failed to delete watch area. Please try again.');
                      // Revert optimistic update on error
                      await refetchWatchAreas();
                    }
                  }}
                >
                  Delete Boundary
                </Button>
              </div>
            </DialogHeader>
            <div className="mt-4">
              {selectedWatchAreaModal.applications.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">
                  No development applications found within this watch area.
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-neutral-600">
                    Found {selectedWatchAreaModal.applications.length} application{selectedWatchAreaModal.applications.length !== 1 ? 's' : ''} within this boundary.
                  </p>
                  <div className="grid gap-4">
                    {selectedWatchAreaModal.applications.map((app: any, index: number) => (
                      <div
                        key={app.id || index}
                        className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors cursor-pointer"
                        onClick={() => {
                          if (app.id) {
                            setSelectedApplication(app);
                            setDetailModalOpen(true);
                            setSelectedWatchAreaModal({ open: false, watchArea: null, applications: [] });
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            {app.reference && (
                              <p className="text-xs font-mono text-neutral-500 mb-1">
                                {app.reference}
                              </p>
                            )}
                            {app.title && (
                              <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                                {app.title}
                              </h4>
                            )}
                            {app.address && (
                              <p className="text-xs text-neutral-600 mb-2">
                                📍 {app.address}
                              </p>
                            )}
                            {app.municipality && (
                              <p className="text-xs text-neutral-500">
                                {app.municipality}
                              </p>
                            )}
                          </div>
                          {app.status && (
                            <Badge
                              className={`text-xs ${
                                app.status?.toUpperCase().includes('APPROVED') || app.status?.toUpperCase().includes('ADOPTED')
                                  ? 'bg-green-100 text-green-800'
                                  : app.status?.toUpperCase().includes('DENIED') || app.status?.toUpperCase().includes('WITHDRAW')
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}
                            >
                              {app.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setSelectedWatchAreaModal({ open: false, watchArea: null, applications: [] })}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Watch Area Name Dialog */}
        <Dialog open={watchAreaNameDialog.open} onOpenChange={(open) => !open && setWatchAreaNameDialog({ open: false, geoJSON: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Name Your Watch Area</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                placeholder="Enter watch area name..."
                value={watchAreaName}
                onChange={(e) => setWatchAreaName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && watchAreaName.trim()) {
                    saveWatchArea();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  // Remove the layer if user cancels
                  if (lastCreatedLayerRef.current && featureGroupRef.current) {
                    featureGroupRef.current.removeLayer(lastCreatedLayerRef.current);
                    if (mapRef.current) {
                      mapRef.current.removeLayer(lastCreatedLayerRef.current);
                    }
                    lastCreatedLayerRef.current = null;
                  }
                  setWatchAreaNameDialog({ open: false, geoJSON: null });
                  setWatchAreaName("");
                }}>
                  Cancel
                </Button>
                <Button onClick={saveWatchArea} disabled={!watchAreaName.trim()}>
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}